import React from 'react';
import {
  formatHours,
  formatCost,
  isRoundingActive,
  normaliseRounding,
  describeRoundingSource,
} from '../lib/rounding';

/**
 * Display a single rounded Effort / Cost metric.
 *
 * Renders the rounded value prominently. When rounding is actually
 * active (i.e. it changes the raw value), also renders a tiny
 * "Rounded" badge next to the value with a tooltip that surfaces
 * the exact underlying number and the source of the rounding rule
 * (per-estimate override vs. profile default).
 *
 * Props:
 *  - kind:        'hours' | 'cost'
 *  - value:       raw numeric value (precise)
 *  - rounding:    resolved rounding config { hours_increment, cost_increment }
 *  - estimate:    estimate doc (used to describe source in tooltip)
 *  - user:        user doc (used to describe source in tooltip)
 *  - symbol:      optional currency symbol for cost
 *  - className:   wrapper class for the value span
 *  - badgeClassName: override class for the badge
 *  - testId:      data-testid for the value span
 */
export default function RoundedMetric({
  kind,
  value,
  rounding,
  estimate,
  user,
  symbol = '$',
  currencyCode,
  className = '',
  badgeClassName = '',
  testId,
  showRaw = false,
  hideBadge = false,
}) {
  const raw = Number(value || 0);
  const norm = normaliseRounding(rounding);
  const active = isRoundingActive(norm) && !showRaw;
  const source = describeRoundingSource(estimate, user);

  // Currency code prefix is only useful when the symbol alone is ambiguous
  // (plain "$" could be USD / CAD / AUD / etc.). For locale-specific symbols
  // that already uniquely identify the currency (A$, C$, £, €, ¥, ₹, etc.)
  // the code is redundant — the user explicitly asked us to drop it.
  // Hours: never code-prefixed.
  const codePrefix = (() => {
    if (kind !== 'cost' || !currencyCode) return '';
    if (!symbol || symbol === '$') return `${currencyCode} `;
    return '';
  })();

  const displayStr = showRaw
    ? (kind === 'hours'
        ? `${raw.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`
        : `${codePrefix}${symbol}${raw.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}`)
    : (kind === 'hours'
        ? `${formatHours(raw, norm)} hrs`
        : `${codePrefix}${formatCost(raw, norm, symbol)}`);

  // Exact underlying value, always precise.
  const exactStr =
    kind === 'hours'
      ? `${raw.toLocaleString(undefined, { maximumFractionDigits: 2 })} hrs`
      : `${codePrefix}${symbol}${raw.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;

  const incrementLabel =
    kind === 'hours'
      ? norm.hours_increment === 0.5
        ? 'nearest 0.5 hr'
        : `nearest ${norm.hours_increment} hr${norm.hours_increment === 1 ? '' : 's'}`
      : `nearest ${symbol}${norm.cost_increment.toLocaleString()}`;

  const tooltip = active
    ? `Exact: ${exactStr}\nRounded to ${incrementLabel}${source ? ` (from ${source})` : ''}`
    : null;

  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid={testId}>
      <span>{displayStr}</span>
      {active && !hideBadge && (
        <span
          title={tooltip}
          aria-label={tooltip}
          className={
            badgeClassName
            || 'cursor-help text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-full bg-[#4ABFCF]/10 text-[#0F2040] border border-[#4ABFCF]/30 hover:bg-[#4ABFCF]/20 transition-colors'
          }
          data-testid={testId ? `${testId}-rounded-badge` : 'rounded-badge'}
        >
          Rounded
        </span>
      )}
    </span>
  );
}
